import React from "react"
import ReactMarkdown from "react-markdown"
import remarkBreaks from "remark-breaks"
import remarkGfm from "remark-gfm"

type Props = {
  content: string
  variant: "assistant" | "user"
}

/**
 * Renders chat message body as GitHub-flavored Markdown (lists, code, links, tables).
 * remark-breaks turns single newlines into line breaks like typical chat UIs.
 */
export default function PortalAiAssistantChatMarkdown({ content, variant }: Props) {
  const user = variant === "user"

  const link =
    "font-medium underline decoration-1 underline-offset-2 transition-colors " +
    (user ? "text-teal-100 hover:text-white" : "text-teal-600 hover:text-teal-800")

  const inlineCode =
    "rounded px-1 py-0.5 font-mono text-[0.9em] " + (user ? "bg-white/20 text-white" : "bg-gray-100 text-gray-900")

  const blockPre =
    "mb-2 max-w-full overflow-x-auto rounded-lg p-3 font-mono text-xs leading-relaxed " +
    (user ? "bg-black/25 text-gray-100" : "bg-gray-900 text-gray-100")

  const thTd = user ? "border border-white/20 px-2 py-1 text-left" : "border border-gray-200 px-2 py-1 text-left"

  return (
    <div className={`max-w-none text-sm ${user ? "text-white" : "text-gray-900"}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{
          p: ({ children }) => <p className="mb-2 last:mb-0 [&:first-child]:mt-0">{children}</p>,
          ul: ({ children }) => <ul className="mb-2 ml-4 list-disc space-y-1 last:mb-0">{children}</ul>,
          ol: ({ children }) => <ol className="mb-2 ml-4 list-decimal space-y-1 last:mb-0">{children}</ol>,
          li: ({ children }) => <li className="leading-snug">{children}</li>,
          strong: ({ children }) => <strong className="font-bold text-inherit">{children}</strong>,
          em: ({ children }) => <em className="italic text-inherit">{children}</em>,
          a: ({ href, children }) => (
            <a href={href} className={link} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
          h1: ({ children }) => <h1 className="mb-2 mt-3 text-base font-semibold first:mt-0">{children}</h1>,
          h2: ({ children }) => <h2 className="mb-2 mt-3 text-sm font-semibold first:mt-0">{children}</h2>,
          h3: ({ children }) => <h3 className="mb-1 mt-2 text-sm font-semibold first:mt-0">{children}</h3>,
          blockquote: ({ children }) => (
            <blockquote
              className={
                "mb-2 border-l-2 pl-3 italic " +
                (user ? "border-white/40 text-teal-50" : "border-gray-300 text-gray-600")
              }>
              {children}
            </blockquote>
          ),
          hr: () => <hr className={`my-3 border-t ${user ? "border-white/25" : "border-gray-200"}`} />,
          pre: ({ children }) => <pre className={blockPre}>{children}</pre>,
          code: ({ className, children, ...rest }) => {
            const isFence = typeof className === "string" && className.includes("language-")
            if (isFence) {
              return (
                <code className={className} {...rest}>
                  {children}
                </code>
              )
            }
            return (
              <code className={inlineCode} {...rest}>
                {children}
              </code>
            )
          },
          table: ({ children }) => (
            <div className="mb-2 max-w-full overflow-x-auto">
              <table className="min-w-full border-collapse text-left text-xs">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className={user ? "bg-white/10" : "bg-gray-50"}>{children}</thead>,
          th: ({ children }) => <th className={thTd}>{children}</th>,
          td: ({ children }) => <td className={thTd}>{children}</td>
        }}>
        {content}
      </ReactMarkdown>
    </div>
  )
}
